
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { XIcon } from './icons/PlaybackIcons';
import { wahooIntegrationService } from '../services/wahooIntegrationService';
import { hammerheadIntegrationService } from '../services/hammerheadIntegrationService';

// Simple CheckCircle Icon since it wasn't exported in PlaybackIcons originally or might be missing
const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
    </svg>
);

interface WelcomeProfileModalProps {
    isOpen: boolean;
    onClose: (didSave: boolean) => void;
}

const WelcomeProfileModal: React.FC<WelcomeProfileModalProps> = ({ isOpen, onClose }) => {
    const { currentUser } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [teamName, setTeamName] = useState('');
    const [heightFeet, setHeightFeet] = useState<number | ''>('');
    const [heightInches, setHeightInches] = useState<number | ''>('');
    const [weightLbs, setWeightLbs] = useState<number | ''>('');
    const [age, setAge] = useState<number | ''>('');
    const [sex, setSex] = useState<'' | 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say'>('');
    const [isSaving, setIsSaving] = useState(false);

    // Pre-fill from Google Auth if available and empty
    // Hook must be at top level, before any return statements
    React.useEffect(() => {
        if (isOpen && currentUser?.displayName && !firstName && !lastName) {
            const parts = currentUser.displayName.split(' ');
            if (parts.length > 0) setFirstName(parts[0]);
            if (parts.length > 1) setLastName(parts.slice(1).join(' '));
        }
    }, [currentUser, firstName, lastName, isOpen]);

    const saveProfileData = async () => {
        if (!currentUser) return false;
        try {
            await userService.updateUserProfile(currentUser.uid, {
                firstName,
                lastName,
                teamName,
                heightFeet: heightFeet === '' ? 0 : heightFeet,
                heightInches: heightInches === '' ? 0 : heightInches,
                weightLbs: weightLbs === '' ? 0 : weightLbs,
                age: age === '' ? 0 : age,
                sex,
                email: currentUser.email || '',
            });

            if (firstName && lastName) {
                await currentUser.updateProfile({
                    displayName: `${firstName} ${lastName}`.trim()
                });
            }
            return true;
        } catch (error) {
            console.error("Error saving profile:", error);
            alert("Failed to save profile. Please try again.");
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        const success = await saveProfileData();
        if (success) {
            onClose(true);
        } else {
            setIsSaving(false);
        }
    };

    const handleConnect = async (service: 'wahoo' | 'hammerhead') => {
        setIsSaving(true);
        const success = await saveProfileData();
        if (success) {
            // Dismiss the modal so it doesn't pop up again when they return from OAuth
            if (currentUser) {
                localStorage.setItem(`raceLineIQ_welcome_dismissed_${currentUser.uid}`, 'true');
            }
            if (service === 'wahoo') wahooIntegrationService.connect();
            else if (service === 'hammerhead') hammerheadIntegrationService.connect();
        } else {
            setIsSaving(false);
        }
    };

    // Conditional rendering MUST happen after all hooks
    if (!isOpen || !currentUser) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[3000] p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[95vh] border border-gray-700 overflow-y-auto relative flex flex-col">
                
                {/* Close Button */}
                <button 
                    onClick={() => onClose(false)} 
                    className="absolute top-4 right-4 text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors z-10"
                    aria-label="Close modal"
                >
                    <XIcon className="w-6 h-6" />
                </button>

                {/* Header Graphic */}
                <div className="bg-gradient-to-r from-cyan-900 to-gray-800 p-6 text-center border-b border-gray-700">
                    <div className="mx-auto bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 border border-cyan-500/50">
                        <CheckIcon className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Welcome to RaceLine<span className="text-cyan-400">IQ</span>!</h2>
                    <p className="text-gray-400 mt-2 text-sm">
                        Let's get your rider profile set up so you can track your stats and compare performance.
                    </p>
                </div>

                <div className="p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">First Name</label>
                                <input 
                                    type="text" 
                                    required
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="Jane"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Last Name</label>
                                <input 
                                    type="text" 
                                    required
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="Doe"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Team Name <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                            <input 
                                type="text" 
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                placeholder="e.g. Velocity Racing"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Height (Ft) <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                                <input 
                                    type="number" 
                                    value={heightFeet}
                                    onChange={(e) => setHeightFeet(e.target.value ? parseFloat(e.target.value) : '')}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="e.g. 5"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Height (In) <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                                <input 
                                    type="number" 
                                    value={heightInches}
                                    onChange={(e) => setHeightInches(e.target.value ? parseFloat(e.target.value) : '')}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="e.g. 10"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Weight (lbs) <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                                <input 
                                    type="number" 
                                    value={weightLbs}
                                    onChange={(e) => setWeightLbs(e.target.value ? parseFloat(e.target.value) : '')}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="e.g. 150"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Age <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                                <input 
                                    type="number" 
                                    value={age}
                                    onChange={(e) => setAge(e.target.value ? parseFloat(e.target.value) : '')}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                    placeholder="e.g. 30"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Sex <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                            <div className="relative">
                                <select 
                                    value={sex}
                                    onChange={(e) => setSex(e.target.value as '' | 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say')}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all appearance-none"
                                >
                                    <option value="" disabled>Select...</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Non-binary">Non-binary</option>
                                    <option value="Prefer not to say">Prefer not to say</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 pb-2 border-t border-gray-700 mt-4">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-3">Connect Your Accounts</label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleConnect('wahoo')}
                                    disabled={isSaving}
                                    className="flex flex-col items-center justify-center p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg border border-gray-600 transition-colors disabled:opacity-50"
                                >
                                    <span className="font-bold text-white text-sm">Wahoo</span>
                                    <span className="text-xs text-blue-400 mt-1">Connect</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleConnect('hammerhead')}
                                    disabled={isSaving}
                                    className="flex flex-col items-center justify-center p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg border border-gray-600 transition-colors disabled:opacity-50"
                                >
                                    <span className="font-bold text-white text-sm">Hammerhead</span>
                                    <span className="text-xs text-indigo-400 mt-1">Connect</span>
                                </button>
                                <button
                                    type="button"
                                    disabled
                                    className="flex flex-col items-center justify-center p-3 bg-gray-800/50 rounded-lg border border-gray-700 opacity-50 cursor-not-allowed"
                                >
                                    <span className="font-bold text-gray-300 text-sm">Garmin</span>
                                    <span className="text-xs text-gray-500 mt-1">Coming Soon</span>
                                </button>
                            </div>
                            <p className="text-center text-xs text-gray-500 mt-3">
                                Connecting an account will save your profile and redirect you to authorize access.
                            </p>
                        </div>

                        <div className="pt-2">
                             <button
                                type="submit"
                                disabled={isSaving}
                                className="w-full bg-cyan-600 text-white font-bold py-3 rounded-lg hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-900/20 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSaving ? 'Saving Profile...' : 'Complete Setup'}
                            </button>
                            
                            <button
                                type="button"
                                onClick={() => onClose(false)}
                                className="w-full mt-3 text-sm text-gray-500 hover:text-gray-300 py-2 transition-colors"
                            >
                                Skip for now
                            </button>
                        </div>
                    </form>
                    <p className="text-center text-xs text-gray-600 mt-4">
                        You can add more details like your USA Cycling license number and privacy zones later in your Profile settings.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WelcomeProfileModal;
